import { useCallback, useContext, useState } from "react";

import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { buildStopPostalCode } from "~/features/quote/utils/build-stop-postal-code.ts";
import { resolveAddressBookCountry } from "~/features/quote/utils/resolve-address-book-country.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { Country } from "~/shared/schemas/location-state-schema.ts";
import type {
	QuoteStateType,
	SubGroupKey,
} from "~/shared/types/quote-details-types.ts";
import {
	ddUseAddressBook,
	toDdWorkflow,
} from "~/shared/utils/datadog-actions.ts";

/**
 * Applies a saved address's location to the quote state — mirroring the writes
 * the AddressBookModal used to perform internally before it was decoupled.
 * Keeps postal code and country moving together so a cross-country selection
 * doesn't leave the previous country winning. `countries` is provided by the
 * caller (which already loads it) to keep this hook free of data fetching.
 */
export const useApplyAddressBookLocation = (
	name: SubGroupKey,
	countries: Country[],
) => {
	const { setSelectedAddressBookItem } = useQuoteAddressContext();
	const { onChange, workflow } = useContext(QuoteDetailsContext);
	const section = name === "pickup" ? "pickup" : "drop";
	const [invalidCountryAddress, setInvalidCountryAddress] =
		useState<AddressBookItemSummary | null>(null);

	const clearInvalidCountryAddress = useCallback(() => {
		setInvalidCountryAddress(null);
	}, []);

	const trackUse = useCallback(() => {
		ddUseAddressBook(toDdWorkflow(workflow), section);
	}, [workflow, section]);

	/** Returns false (applying nothing) when the address's country can't be resolved. */
	const applyLocation = useCallback(
		(address: AddressBookItemSummary): boolean => {
			const hasCountry =
				address.country != null && address.country.trim() !== "";
			const selectedCountry = resolveAddressBookCountry(address, countries);
			if (hasCountry && countries.length > 0 && !selectedCountry) {
				setInvalidCountryAddress(address);
				return false;
			}
			setSelectedAddressBookItem(name, address);
			onChange(
				`${name}PostalCode` as keyof QuoteStateType,
				buildStopPostalCode(address),
			);
			if (selectedCountry) {
				onChange(`${name}Country` as keyof QuoteStateType, selectedCountry);
			}
			trackUse();
			return true;
		},
		[countries, name, onChange, setSelectedAddressBookItem, trackUse],
	);

	return {
		applyLocation,
		clearInvalidCountryAddress,
		invalidCountryAddress,
		trackUse,
	};
};
